/**
 * Extracting a displayable message from a failed Teamio API call.
 *
 * WHY THIS EXISTS — verified against the deployed Lambdas on 2026-07-30:
 *
 * `/opt/common`'s createErrorResponse is `body: JSON.stringify(message)`. Almost
 * every handler passes a STRING, so the HTTP body is a bare JSON string and axios
 * (0.18.0, which JSON.parses any string body) hands back:
 *
 *     err.response.data === 'siteID is required'      // a string, NOT an object
 *
 * So the near-universal frontend idiom `err.response?.data?.error` is ALWAYS
 * undefined and every one of those call sites silently falls through to
 * `err.message` — i.e. axios's "Request failed with status code 400". No backend
 * error text has ever reached the user on those paths. It hid for years because
 * the fallback always produces a confident-looking English sentence, and because
 * the service unit-test mocks return `{ error: msg }`, a shape production never sends.
 *
 * Three response shapes exist in the wild:
 *   1. bare string            — ~927 call sites (createErrorResponse(code, 'msg'))
 *   2. { message }            — API Gateway / CloudFront errors, and 23 handlers
 *   3. { error, message }     — 2 handlers (online-scheduling), where `error` is a
 *                               machine CODE like 'SETTINGS_PARSE_ERROR'
 *
 * createSuccessResponse is unaffected: success payloads are objects, so `data.field`
 * works there. That asymmetry — same serialiser, different argument type — is the
 * root cause, and it is invisible at the call site.
 */

/**
 * Longest string we will put in front of a user. Real handler messages top out
 * around 170 characters; anything beyond this is not a message.
 */
export const MAX_MESSAGE_LENGTH = 500;

/**
 * How much of a response body to keep in the diagnostic log. Generous enough to
 * identify an HTML error page or a stack dump, bounded so a WAF page cannot flood
 * the console or the LogRocket recording.
 */
const LOG_BODY_LIMIT = 2000;

/**
 * Is this candidate fit to display?
 *
 * The hazard is not our own Lambdas — it is the rest of the response path. These
 * requests traverse CloudFront and API Gateway, and when one of THOSE errors (WAF
 * block, 403, throttle) the body is text/html. axios fails to JSON.parse it and
 * returns the raw page as a perfectly valid non-empty string. Without this guard a
 * modal would render an entire HTML error page as the reason something failed.
 */
export function isPresentableMessage(value) {
  if (typeof value !== 'string') return false;
  const text = value.trim();
  if (!text) return false;
  if (text.length > MAX_MESSAGE_LENGTH) return false; // HTML page, stack dump, blob
  // Reject '<' ANYWHERE, not merely as a prefix. Two consumers of this value render
  // it with v-html -- ErrorDisplay.vue:21 and PopupErrorDisplay.vue:21 -- so any
  // returned markup becomes live DOM. That sink is pre-existing but historically
  // received only the axios Error OBJECT, which stringifies to the inert
  // "Error: Request failed with status code NNN"; routing real server strings into
  // it is new, and main-service returns raw err.message in 9 places (a driver or
  // SQL exception can echo user-supplied data). A repo-wide scan found ZERO
  // createErrorResponse messages containing '<', so this costs no real message.
  if (text.includes('<')) return false;
  return true;
}

/**
 * Does this read as an internal/programmer-facing message rather than something a
 * dental practice should see?
 *
 * Audited 2026-07-30 across the 7 services reachable from the migrated call sites:
 * 280 literal createErrorResponse messages, 116 flagged. Nearly all are
 * argument validation ("siteID is required", "Missing required parameter: siteID")
 * which a user CANNOT trigger by clicking — they mean the frontend sent a malformed
 * request. Before the extractApiError migration these were invisible, because the
 * frontend read `data.error` off a bare string and always fell through. Now they
 * would surface verbatim.
 *
 * Rather than rewrite 116 strings across 7 services (most unreachable, all needing
 * deploys), we drop them here and let the call site's own practice-appropriate
 * fallback show instead — "Failed to load terminals" beats "deviceId is required".
 *
 * Deliberately NARROW. It must not swallow genuinely useful text like
 * "This payment link has expired" or "Another card charge for this patient is
 * still running on a terminal". Only unmistakable internal shapes are matched.
 */
// Case-insensitive: handlers spell these inconsistently (siteID / SiteID / siteid).
// None of these tokens appear in legitimate practice-facing copy.
const INTERNAL_IDENTIFIER = /\b(siteID|patNum|paymentID|deviceId|apiKey|businessId|formIDForWeb|formSubmissionIDForWeb|formIDs|wikiPageID|treatPlanNum|channelID|isPinned|optionId|resourceId|referenceId)\b/i;
const INTERNAL_PREFIX = /^(missing required (parameter|field)s?\b|invalid payload\b|invalid json\b|webhook error\b)/i;
const MACHINE_CODE = /^[A-Z][A-Z0-9_]{3,}$/;

export function looksLikeInternalMessage(value) {
  if (typeof value !== 'string') return false;
  const text = value.trim();
  return MACHINE_CODE.test(text) || INTERNAL_PREFIX.test(text) || INTERNAL_IDENTIFIER.test(text);
}

/**
 * Best displayable message from a failed axios call, or `fallback`.
 *
 * Candidates are tried in priority order and the first PRESENTABLE one wins —
 * deliberately not the first non-empty one, so an HTML body can never be rendered.
 *
 * NOTE: this returns text for HUMANS. Do not use it to branch on machine codes —
 * for `{ error: 'SOME_CODE' }` responses read `err.response.data.error` directly.
 */
export function extractApiError(err, fallback = 'Something went wrong. Please try again.') {
  const data = err && err.response && err.response.data;

  const candidates = [];
  if (typeof data === 'string') candidates.push(data);
  else if (data && typeof data === 'object') candidates.push(data.error, data.message);

  // err.message is used ONLY when the server never responded. With a response it is
  // always axios boilerplate ("Request failed with status code 502") — developer
  // text, and strictly worse for a user than the call site's own fallback copy
  // ("Failed to load terminals"). So a server that responded with nothing usable
  // falls through to `fallback`, not to boilerplate.
  if (!(err && err.response)) candidates.push(err && err.message);

  let shown = null;
  let withheld = null;
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null || candidate === '') continue;
    if (!isPresentableMessage(candidate)) { withheld = withheld || 'unpresentable-body'; continue; }
    if (looksLikeInternalMessage(candidate)) { withheld = 'internal-message'; continue; }
    shown = candidate.trim();
    break;
  }

  if (shown === null) {
    shown = fallback;
    withheld = withheld || 'no-message-from-server';
  } else {
    withheld = null; // the server's own wording reached the user
  }

  logApiFailure(err, shown, withheld);
  return shown;
}

/**
 * Full technical record of a failed API call.
 *
 * Softening what the USER sees must never soften what WE can see. Whenever this
 * helper substitutes friendly copy, the original body, status, method, URL and the
 * error object (which carries the stack) still go to the console — and therefore to
 * LogRocket session replay — so an incident is diagnosable from the recording alone.
 *
 * Logged on every failure, not only on substitution: a call site that shows the
 * server's own wording still benefits from having status/URL/stack attached, and
 * several of the migrated call sites did no logging of their own.
 */
function logApiFailure(err, shown, withheld) {
  try {
    const response = err && err.response;
    const config = (err && err.config) || {};
    let body = response ? response.data : undefined;
    if (typeof body === 'string' && body.length > LOG_BODY_LIMIT) {
      body = `${body.slice(0, LOG_BODY_LIMIT)}…[truncated ${body.length} chars]`;
    }
    // Error object passed last so devtools and LogRocket capture the stack trace.
    console.error('[apiError] API call failed', {
      status: response ? response.status : null,
      method: config.method ? String(config.method).toUpperCase() : null,
      url: config.url || null,
      code: (err && err.code) || null,
      responseBody: body,
      shownToUser: shown,
      // null when the user is reading the server's own wording; otherwise WHY we
      // replaced it — the single most useful field when a report says "it just
      // said Failed to load terminals".
      withheldReason: withheld,
    }, err);
  } catch (loggingError) {
    // Diagnostics must never break the UI they are describing.
    try { console.error('[apiError] failed to log API failure', loggingError); } catch (_) { /* give up */ }
  }
}
